/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useMemo, useState } from "react";
import { useLocation } from 'react-router-dom';
// prop-types is a library for typechecking of props

// react-table components
import React from 'react';
import {
  useAsyncDebounce,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
// @mui material components
import Autocomplete from "@mui/material/Autocomplete";
import Icon from "@mui/material/Icon";
import TableContainer from "@mui/material/TableContainer";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React examples
import { Skeleton, Stack } from "@mui/material";
import MDButton from "components/MDButton";
import { handleShiftKeySelection } from "utils/rowSelection";
import { TableData } from "./TableData";

function DataTable({
  entriesPerPage,
  canSearch,
  canRefresh,
  canExport,
  searchFun,
  showTotalEntries = true,
  table,
  pagination,
  isSorted,
  noEndBorder,
  onRowClick,
  loader = false,
  deepSearch,
  isSelection,
  isMultiSelect = true,
  selectedRows,
  setSelectedRows,
  tableHeight = "calc(100vh - 220px)",
  fixedWidth = false,
  canPagination = true,
  onRowRedirect,
  canDelete = false,
  deleteFunc,
  exportFunc,
  searchIds = () => {},
  customSearch = false,
  onCustomSearchChange
}) {
  const location = useLocation();
  const currentPath = location.pathname;
  const defaultValue = entriesPerPage?.defaultValue ? entriesPerPage?.defaultValue : 25;
  const entries = entriesPerPage?.entries ? entriesPerPage?.entries?.map((el) => el.toString()) : ["5", "10", "15", "20", "25", "50", "100"];

  let originalColumns = table.columns;

  const columns = useMemo(() => originalColumns.filter((column) => column.show !== false), [table]);

  const data = useMemo(() => table.rows, [table]);

  const savedPageNumber = 0;
  let savedSortBy;
  const sortByString = localStorage.getItem("tableSortBy");

  const [initialRowIndex, setInitialRowIndex] = useState(null);
  const [currentRowIndex, setCurrentRowIndex] = useState(null);
  const [selectedAll, setSelectedAll] = useState(false);

  if (sortByString) {
    try {
      savedSortBy = JSON.parse(sortByString);
    } catch (error) {
      savedSortBy = [
        { id: "eventdate", desc: true },
        { id: "date", desc: true },
        { id: "request_date", desc: true },
      ];
    }
  } else {
    savedSortBy = [
      { id: "eventdate", desc: true },
      { id: "date", desc: true },
      { id: "request_date", desc: true },
    ];
  }

  const globalFilter = React.useCallback((rows, ids, query) => {
    const searchTerm = String(query);
    return rows.filter((row) => {
      if (deepSearch) {
        return JSON.stringify(row.original).includes(searchTerm) == true;
        // return searchDynamoDBEvent(row.original, searchTerm) == true
      }
      const matches = ids.filter((id) => {
        const rowValue = row.values[id];
        let searchTermLower = searchTerm.toLowerCase();

        if (searchTerm == '"') {
          // ignore first ' " ' handle it as no input
          return true;
        } else if (searchTerm.startsWith('"') && searchTerm.endsWith('"')) {
          const exactSearchTerm = searchTerm.slice(1, -1);
          return String(rowValue) === exactSearchTerm;
        } else if (searchTerm.startsWith('"')) {
          // ignore until ending ' " ' has been entered
          searchTermLower = searchTerm.slice(1);
        }

        return rowValue !== undefined ? String(rowValue).toLowerCase().includes(searchTermLower.toLowerCase()) : false;
      });

      return matches.length > 0;
    });
  }, []);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: savedPageNumber,
        // sortBy: savedSortBy,
      },
      globalFilter,
    },
    useGlobalFilter,
    useSortBy,
    ...(canPagination ? [usePagination] : [])
  );

  const {
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter: globalFilters, sortBy },
  } = tableInstance;

  const displayedRows = canPagination ? page : rows;

  useEffect(() => {
    if (canPagination) {
      setPageSize(defaultValue || 25);
    }
  }, [defaultValue]);
  useEffect(() => {
    localStorage.setItem("tableSortBy", {});
  }, [currentPath]);

  // Save sort state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("tableSortBy", JSON.stringify(sortBy));
  }, [sortBy]);

  useEffect(() => {
    if(!selectedRows?.length){
      setSelectedAll(false)
    }
  }, [selectedRows])

  // useEffect(() => {
  //   localStorage.setItem("currentPage", 0);
  // }, [currentPath]);

  useEffect(() => {
    const selectRowHandler = (index) => {
      setSelectedRows((prevSelectedRows) => {
        const rowData = displayedRows[index]?.original.id;
        if (prevSelectedRows.includes(rowData)) {
          return prevSelectedRows.filter((row) => row !== rowData);
        } else {
          return [...prevSelectedRows, rowData];
        }
      });
    };

    const handleKeyDown = (event) => {
      handleShiftKeySelection({
        event,
        currentRowIndex,
        initialRowIndex,
        pageLength: displayedRows.length,
        selectRowHandler,
        setCurrentRowIndex,
      });
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentRowIndex]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPageSize(value);

  // Render the paginations
  const renderPagination =
    canPagination && pageOptions.length > 1
      ? pageOptions.map((option) => (
          <MDPagination
            item
            key={option}
            onClick={() => {
              gotoPage(Number(option));
              // localStorage.setItem("currentPage", Number(option));
            }}
            active={pageIndex === option}
          >
            {option + 1}
          </MDPagination>
        ))
      : null;

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) => {
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));
    // localStorage.setItem("currentPage", Number(value.value - 1));
  };

  // Customized page options starting from 1
  const customizedPageOptions = canPagination ? pageOptions.map((option) => option + 1) : [];

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilters);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    if(customSearch){
      onCustomSearchChange(value || undefined)
    } else {
      setGlobalFilter(value || undefined);
    }
  }, 500);

  useEffect(() => {
    if(globalFilters){
      let ids = rows.map((e) => e.values.id)
      searchIds(ids)
    } else {
      searchIds([])
    }
  }, [globalFilters])

  const customPreviousPage = () => {
    previousPage();
    // localStorage.setItem("currentPage", pageIndex - 1);
  };

  const customNextPage = () => {
    nextPage();
    // localStorage.setItem("currentPage", pageIndex + 1);
  };

  // Setting the entries starting point
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions?.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  return (
    <TableContainer
      sx={{
        maxHeight: tableHeight,
        display: "flex",
        flexDirection: "column",
        boxShadow: "none",
      }}
    >
      {/* Header */}
      {(entriesPerPage || canSearch || canRefresh) && (
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pl={3}
          pr={3}
          pt={1}
          pb={1}
          sx={{ flexShrink: 0, backgroundColor: "white", position: "sticky", flexWrap: "wrap", gap: 1, top: 0, zIndex: 2 }}
        >
          {entriesPerPage && canPagination && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize?.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10));
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography
                variant="caption"
                color="secondary"
                sx={{
                  marginLeft: { xs: "2px" },
                  marginRight: { xs: "2px" },
                }}
              >
                <MDTypography sx={{ display: { xs: "none" } }}>&nbsp;&nbsp;</MDTypography>
                entries per page
              </MDTypography>
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" sx={{ ml: "auto", flexWrap: "nowrap" }}>
            {canRefresh && (
              <MDBox width="4rem" ml="auto" style={{ marginRight: "10px" }}>
                <MDButton size="small" variant="gradient" color="dark" fullWidth onClick={() => (!loader ? searchFun() : {})}>
                  <i className={"fas fa-refresh"}></i>
                </MDButton>
              </MDBox>
            )}
            {(canDelete && selectedRows.length) ? (
              <MDBox width="4rem" ml="auto" style={{ marginRight: "10px" }}>
                <MDButton size="small" variant="gradient" color="error" fullWidth onClick={() => (!loader ? deleteFunc() : {})} style={{ backgroundColor: "darkred" }}>
                  Delete
                </MDButton>
              </MDBox>
            ) : <></>}
            {canExport && table.rows.length > 0 && (
              <MDBox width="4rem" ml="auto" style={{ marginRight: "10px" }}>
                <MDButton size="small" variant="gradient" color="dark" fullWidth onClick={() => (!loader ? exportFunc() : {})}>
                  <i className={"fas fa-file-export"}></i>
                </MDButton>
              </MDBox>
            )}
            {canSearch && (
              <MDBox width="12rem" ml="auto">
                <MDInput
                  placeholder="Search..."
                  value={search}
                  size="small"
                  fullWidth
                  onChange={({ currentTarget }) => {
                    setSearch(currentTarget.value);
                    onSearchChange(currentTarget.value);
                  }}
                />
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      )}

      {/* Scrollable Table Data */}
      <MDBox
        sx={{
          flexGrow: 1,
          overflow: "auto",
        }}
      >
        {!loader ? (
          <TableData
            tableInstance={tableInstance}
            initialRowIndex={initialRowIndex}
            displayedRows={displayedRows}
            isSelection={isSelection}
            isMultiSelect={isMultiSelect}
            selectedAll={selectedAll}
            isSorted={isSorted}
            selectedRows={selectedRows}
            fixedWidth={fixedWidth}
            noEndBorder={noEndBorder}
            setSelectedRows={setSelectedRows}
            onRowClick={onRowClick}
            onRowRedirect={onRowRedirect}
            setSelectedAll={setSelectedAll}
            setInitialRowIndex={setInitialRowIndex}
            setCurrentRowIndex={setCurrentRowIndex}
          />
        ) : (
          <StackSkeleton />
        )}
      </MDBox>

      {/* Footer */}
      {canPagination && pageOptions.length > 1 && (
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          pt={!showTotalEntries && pageOptions.length === 1 ? 0 : 1}
          pb={!showTotalEntries && pageOptions.length === 1 ? 0 : 1}
          pl={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
          pr={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
          sx={{ flexShrink: 0, backgroundColor: "white", position: "sticky", bottom: 0, zIndex: 2 }}
        >
          {showTotalEntries && (
            <MDBox mb={{ xs: 3, sm: 0 }}>
              <MDTypography variant="button" color="secondary" fontWeight="regular">
                Showing {entriesStart} to {entriesEnd} of {rows.length} entries
              </MDTypography>
            </MDBox>
          )}
          {pageOptions.length > 1 && (
            <MDPagination
              variant={pagination?.variant ? pagination?.variant : "gradient"}
              color={pagination?.color ? pagination?.color : "info"}
            >
              {canPreviousPage && (
                <MDPagination item onClick={() => customPreviousPage()}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </MDPagination>
              )}
              {renderPagination.length > 6 ? (
                <MDBox width="5rem" mx={1}>
                  <MDInput
                    inputProps={{
                      type: "number",
                      min: 1,
                      max: customizedPageOptions.length,
                    }}
                    value={customizedPageOptions[pageIndex]}
                    onChange={(handleInputPagination, handleInputPaginationValue)}
                  />
                </MDBox>
              ) : (
                renderPagination
              )}
              {canNextPage && (
                <MDPagination item onClick={() => customNextPage()}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </MDPagination>
              )}
            </MDPagination>
          )}
        </MDBox>
      )}
    </TableContainer>
  );
}

function StackSkeleton() {
  return (
    <Stack spacing={0.5}>
      <Skeleton variant="rectangular" sx={{ fontSize: "4rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
    </Stack>
  );
}

export default React.memo(DataTable);
